import { sendGTMEvent } from "@next/third-parties/google";
import type { Cart, LineItem, Order, Product, Variant } from "@spree/sdk";

/** `sku` is added to Store API line items by the backend (Tkf::Api::V3::LineItemSerializer). */
type LineItemWithSku = LineItem & { sku?: string | null };

interface GA4Item {
  item_id: string;
  item_name: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  index?: number;
  item_list_id?: string;
  item_list_name?: string;
  item_category?: string;
  discount?: number;
  coupon?: string;
}

interface ItemMappingOptions {
  index?: number;
  listId?: string;
  listName?: string;
  variant?: Variant | null;
}

function safeParseFloat(value: string | undefined | null): number {
  const parsed = parseFloat(value as string);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function mapProductToGA4Item(
  product: Product,
  options: ItemMappingOptions = {},
): GA4Item {
  const { index, listId, listName, variant } = options;

  const price = variant?.price ?? product.price;
  const originalPrice = variant?.original_price ?? product.original_price;

  const currentAmount =
    price?.amount != null ? parseFloat(price.amount) : undefined;
  const originalAmount =
    originalPrice?.amount != null
      ? parseFloat(originalPrice.amount)
      : undefined;

  let discount: number | undefined;
  if (
    currentAmount != null &&
    originalAmount != null &&
    originalAmount > currentAmount
  ) {
    discount = originalAmount - currentAmount;
  }

  // item_id mirrors the Google Merchant feed's g:id (variant SKU, else variant
  // id) so GA4 / Google Ads events join up with the feed. `default_variant` is
  // only present when expanded (product page), so listings fall back to its id.
  const idVariant = variant ?? product.default_variant;
  const item: GA4Item = {
    item_id:
      idVariant?.sku ||
      idVariant?.id ||
      product.default_variant_id ||
      product.id,
    item_name: product.name,
    price: currentAmount,
  };

  if (variant?.options_text) {
    item.item_variant = variant.options_text;
  }
  if (discount != null && discount > 0) {
    item.discount = discount;
  }
  if (index != null) {
    item.index = index;
  }
  if (listId) {
    item.item_list_id = listId;
  }
  if (listName) {
    item.item_list_name = listName;
  }
  if (product.categories && product.categories.length > 0) {
    item.item_category = product.categories[0].name;
  }

  return item;
}

export function mapLineItemToGA4Item(
  lineItem: LineItemWithSku,
  options: { index?: number; listId?: string; listName?: string } = {},
): GA4Item {
  const item: GA4Item = {
    // Same id as the Merchant feed's g:id (variant SKU, else variant id) — never
    // the line item id, which matches nothing in the feed.
    item_id: lineItem.sku || lineItem.variant_id,
    item_name: lineItem.name,
    price: safeParseFloat(lineItem.price),
    quantity: lineItem.quantity,
  };

  if (lineItem.options_text) {
    item.item_variant = lineItem.options_text;
  }
  if (options.index != null) {
    item.index = options.index;
  }
  if (options.listId) {
    item.item_list_id = options.listId;
  }
  if (options.listName) {
    item.item_list_name = options.listName;
  }

  const discountTotal = safeParseFloat(lineItem.discount_total);
  if (discountTotal < 0) {
    const quantity = Math.max(lineItem.quantity ?? 1, 1);
    item.discount = Math.abs(discountTotal) / quantity;
  }

  return item;
}

function pushEcommerceEvent(
  eventName: string,
  ecommerceData: Record<string, unknown>,
): void {
  sendGTMEvent({ ecommerce: null });
  sendGTMEvent({ event: eventName, ecommerce: ecommerceData });
}

// --- Event Functions ---

let lastViewItemListKey: string | null = null;

export function trackViewItemList(
  products: Product[],
  listId: string,
  listName: string,
  currency: string,
): void {
  if (products.length === 0) return;

  const key = `${listId}:${products.map((p) => p.id).join(",")}`;
  if (lastViewItemListKey === key) return;
  lastViewItemListKey = key;

  pushEcommerceEvent("view_item_list", {
    item_list_id: listId,
    item_list_name: listName,
    currency,
    items: products.map((product, index) =>
      mapProductToGA4Item(product, { index, listId, listName }),
    ),
  });
}

export function trackSelectItem(
  product: Product,
  listId: string,
  listName: string,
  index: number,
  currency: string,
): void {
  pushEcommerceEvent("select_item", {
    item_list_id: listId,
    item_list_name: listName,
    currency,
    items: [mapProductToGA4Item(product, { index, listId, listName })],
  });
}

export function trackViewItem(
  product: Product,
  currency: string,
  variant?: Variant | null,
): void {
  const item = mapProductToGA4Item(product, { variant });
  pushEcommerceEvent("view_item", {
    currency,
    value: item.price ?? 0,
    items: [item],
  });
}

export function trackAddToCart(
  product: Product,
  variant: Variant | null,
  quantity: number,
  currency: string,
): void {
  const item = mapProductToGA4Item(product, { variant });
  item.quantity = quantity;
  pushEcommerceEvent("add_to_cart", {
    currency,
    value: (item.price ?? 0) * quantity,
    items: [item],
  });
}

export function trackRemoveFromCart(
  lineItem: LineItem,
  currency: string,
): void {
  pushEcommerceEvent("remove_from_cart", {
    currency,
    value: safeParseFloat(lineItem.total),
    items: [mapLineItemToGA4Item(lineItem)],
  });
}

export function trackViewCart(order: Cart | Order): void {
  pushEcommerceEvent("view_cart", {
    currency: order.currency,
    value: safeParseFloat(order.item_total),
    items:
      order.items?.map((item, index) =>
        mapLineItemToGA4Item(item, { index }),
      ) ?? [],
  });
}

function buildOrderEcommercePayload(
  order: Cart | Order,
  extras?: Record<string, unknown>,
): Record<string, unknown> {
  const coupon = order.discounts?.find((d) => d.code)?.code;
  return {
    currency: order.currency,
    value: safeParseFloat(order.total),
    ...(coupon && { coupon }),
    ...extras,
    items:
      order.items?.map((item, index) =>
        mapLineItemToGA4Item(item, { index }),
      ) ?? [],
  };
}

export function trackBeginCheckout(order: Cart | Order): void {
  pushEcommerceEvent("begin_checkout", buildOrderEcommercePayload(order));
}

export function trackAddShippingInfo(
  order: Cart | Order,
  shippingTier?: string,
): void {
  pushEcommerceEvent(
    "add_shipping_info",
    buildOrderEcommercePayload(order, {
      ...(shippingTier && { shipping_tier: shippingTier }),
    }),
  );
}

export function trackAddPaymentInfo(
  order: Cart | Order,
  paymentType?: string,
): void {
  pushEcommerceEvent(
    "add_payment_info",
    buildOrderEcommercePayload(order, {
      ...(paymentType && { payment_type: paymentType }),
    }),
  );
}

/** Purchases already sent in this page's JS context (survives blocked Web Storage). */
const trackedPurchases = new Set<string>();

function storageHas(storage: () => Storage, key: string): boolean {
  try {
    return storage().getItem(key) !== null;
  } catch {
    return false; // Storage unavailable (private browsing, Safari settings)
  }
}

function storageSet(storage: () => Storage, key: string): void {
  try {
    storage().setItem(key, "1");
  } catch {
    // Storage unavailable — the in-memory guard still covers this page view
  }
}

/**
 * Send `purchase` once per order. Guarded three ways because the order-placed
 * page can fire twice in one page view (seen on iPhone Safari in prd): an
 * in-memory set for this page, sessionStorage for reloads in the tab, and
 * localStorage for revisits (e.g. reopening the confirmation later).
 */
export function trackPurchase(order: Cart | Order): void {
  if (typeof window === "undefined") return;
  const key = `gtm_purchase_${order.number}`;
  if (
    trackedPurchases.has(key) ||
    storageHas(() => window.sessionStorage, key) ||
    storageHas(() => window.localStorage, key)
  ) {
    return;
  }
  trackedPurchases.add(key);

  pushEcommerceEvent(
    "purchase",
    buildOrderEcommercePayload(order, {
      transaction_id: order.number,
      tax: safeParseFloat(order.tax_total),
      shipping: safeParseFloat(order.delivery_total),
    }),
  );

  storageSet(() => window.sessionStorage, key);
  storageSet(() => window.localStorage, key);
}

export function trackQuickSearch(
  products: Product[],
  searchTerm: string,
  currency: string,
): void {
  pushEcommerceEvent("view_search_results", {
    search_term: searchTerm,
    currency,
    items: products.map((product, index) =>
      mapProductToGA4Item(product, {
        index,
        listId: "quick-search",
        listName: "Quick Search",
      }),
    ),
  });
}

let lastViewSearchResultsKey: string | null = null;

export function trackViewSearchResults(
  products: Product[],
  searchTerm: string,
  currency: string,
): void {
  if (products.length === 0) return;

  const key = `${searchTerm}:${products.map((p) => p.id).join(",")}`;
  if (lastViewSearchResultsKey === key) return;
  lastViewSearchResultsKey = key;

  pushEcommerceEvent("view_search_results", {
    search_term: searchTerm,
    currency,
    items: products.map((product, index) =>
      mapProductToGA4Item(product, {
        index,
        listId: "search-results",
        listName: "Search Results",
      }),
    ),
  });
}
